import React, { useCallback, useEffect, useRef, useState } from "react";
import { checkPageVisibility } from "../../../../hooks/utils";
import { getCookie, setCookie, setReceiverCookie, setReceiverSession } from "../../../../utis/helper";
import { usePageVisibility } from "./usePageVisibility";
import {networkService} from "../../../config/axios"

const useStatHook = (isWebsocketConnection) => {
  const { sess_id } = window?.objInit;
  const [trakingData, setTrackingData] = useState({});
  const wsRef = useRef(null);
  const [videoData, setVideoData] = useState({});
  const [actionCableSessionData, setActionCableSessionData] = useState({});
  const [pageNum, setPageNum] = useState(null)
  const optinCookie = getCookie("optin", {});
  const lastSeenPageCookie = getCookie("lastSeenPage", {});
  const updateIntervalRef = useRef(null);
  const sendDataIntervalRef = useRef(null);
  const actionCableSessionDataRef = useRef({});
  const pageCreateDebounceRef = useRef(null);
  // Tracks which page the current debounce is queued for, so initSessionData
  // can decide whether to cancel it (same page) or wait for it (different page).
  const pendingDebouncePageRef = useRef(null);
  const isFocused = usePageVisibility();
  const isPageVisible = useRef(null);
  // Ref so intervals always see the live value without stale closure
  const optinModalOpenRef = useRef(false);

  // Sync state → ref, but preserve seconds_on_page accumulated by the 1s interval
  // since the last state update. Without this, a page-change / ID-received state
  // update would overwrite seconds that the interval already counted forward.
  useEffect(() => {
    const accumulatedSeconds = actionCableSessionDataRef.current?.seconds_on_page;
    const stateSeconds = actionCableSessionData?.seconds_on_page;
    actionCableSessionDataRef.current = {
      // Use the ref as base (not state) so a stale state update from initSessionData
      // cannot overwrite ref.page after updatePage has already advanced it.
      // Only seconds_on_page is synced here; page/id are set directly by their owners.
      ...actionCableSessionDataRef.current,
      seconds_on_page: Math.max(accumulatedSeconds ?? 1, stateSeconds ?? 1),
    };
  }, [actionCableSessionData]);

  const WS_SCHEME = window.location.protocol === 'https:' ? 'wss' : 'ws';
  const WEB_URL = `${WS_SCHEME}://${window.location.host}`
  // Direct page-tracking function — bypasses the DOM event listener entirely.
  // Safe to call multiple times for the same page; the guard prevents duplicates.
  // All other events (pause, click, video, opt-in, seconds interval) use
  // actionCableSessionDataRef directly and are completely unaffected.
  const trackPageChange = (nextPage) => {
    if (!Number.isFinite(nextPage) || nextPage === actionCableSessionDataRef.current?.page) return;

    const prevRef = actionCableSessionDataRef.current;
    const hasPrev = prevRef && Object.keys(prevRef).length > 0;
    const base = hasPrev
      ? prevRef
      : {
          session_id: window?.objInit?.sess_id,
          tracking_link_id: window?.objInit?.tlb?.id,
          doc_id: Number(window?.objInit?.doc_id),
          docs_file_id: Number(
            window?.objInit?.active_files &&
              Object.keys(window?.objInit?.active_files)[0]
          ),
        };
    const currentReceiverCookie = getCookie("receiver", {});
    const data = {
      ...base,
      page: nextPage,
      seconds_on_page: 1,
      id: null,
      ...(currentReceiverCookie[window?.objInit?.tlb?.token]?.id
        ? { receiver_id: currentReceiverCookie[window?.objInit?.tlb?.token].id }
        : {}),
    };

    actionCableSessionDataRef.current = { ...data };
    setActionCableSessionData({ ...data });

    if (pendingDebouncePageRef.current === nextPage && pageCreateDebounceRef.current) {
      clearTimeout(pageCreateDebounceRef.current);
    }
    const dataSnapshot = { ...data };
    pendingDebouncePageRef.current = nextPage;
    pageCreateDebounceRef.current = setTimeout(() => {
      pendingDebouncePageRef.current = null;
      actionCableSendData(dataSnapshot, "save_info");
    }, 50);
  };

  // Fallback DOM-event listener — kept for other components (EditorHeader,
  // SettingsEnableOPTIN) that listen on #pageNumber "change".
  const updatePage = useCallback(
    (event) => {
      trackPageChange(parseInt(event.target.value));
    },
    [actionCableSessionData, pageNum]
  );

  // If the TL has a pre-configured receiver, set the cookie and backfill session pages
  // so pages viewed before any opt-in form still get receiver_id assigned.
  // Mirrors production pdfViewerController.js.erb lines 1281-1286.
  useEffect(() => {
    const objInit = window?.objInit;
    const tlb = objInit?.tlb;
    if (!tlb?.receiver?.id || !tlb?.token) return;
    const existing = getCookie("receiver", {})[tlb.token];
    if (existing) return; // already set
    setReceiverCookie(tlb.token, { id: tlb.receiver.id });
    setReceiverSession(tlb.receiver.id, tlb.id, objInit.sess_id);
  }, []);

  useEffect(() => {
    setTrackingData(window?.objInit);
    if ((sess_id == undefined && !wsRef.current) || !isWebsocketConnection)
      return;
    const websocket = new WebSocket(`${WEB_URL}/cable`);
    wsRef.current = websocket;
    websocket.onopen = (e) => {
      console.log("WebSocket connection established.");

      const subscriptionMessage = JSON.stringify({
        command: "subscribe",
        identifier: JSON.stringify({
          channel: "SessionMonitoringChannel",
          data: { session_id: window?.objInit?.sess_id },
        }),
      });
      websocket.send(subscriptionMessage);
      actionCableSetIntervals();
      initActionCableSessionData(window.objInit?.tlb?.landing_page || 1);

      // Link a returning receiver's cookie to this new session so it shows their
      // name instead of "Unknown". Covers the refresh/revisit case where opt-in
      // was completed in a prior session but receiver_id is not yet set on this one.
      const _objInit = window?.objInit;
      const _tlb = _objInit?.tlb;
      if (_tlb?.token && _objInit?.sess_id) {
        const _receiverData = getCookie("receiver", {})[_tlb.token];
        if (_receiverData?.id) {
          setReceiverSession(_receiverData.id, _tlb.id, _objInit.sess_id);
        }
      }
    };

    websocket.onerror = (e) => {
      console.log("Error in websocket: ", e);
    };

    websocket.onmessage = (e) => {
      try {
        const data = JSON.parse(e.data);
        if (data?.message?.id) {
          const receivedId = data.message.id;
          // Update ref directly first so seconds_on_page already accumulated
          // is NOT overwritten by the subsequent setState → useEffect sync.
          actionCableSessionDataRef.current = {
            ...actionCableSessionDataRef.current,
            id: receivedId,
            session_page_id: receivedId,
          };
          setActionCableSessionData((prevData) => ({
            ...prevData,
            id: receivedId,
            session_page_id: receivedId,
          }));
        }
      } catch (error) {
        console.log('error', error);
      }
    };

    // Flush current session data before the page/tab closes so no seconds are lost
    const handlePageHide = () => {
      const d = actionCableSessionDataRef.current;
      if (d?.page && d?.id) {
        actionCableSendData(d, "save_info");
      }
    };
    window.addEventListener("pagehide", handlePageHide);

    return () => {
      window.onload = null;
      window.removeEventListener("pagehide", handlePageHide);
      websocket.close();
    };
  }, [sess_id]);

  useEffect(() => {
    document
      .getElementById("pageNumber")
      ?.addEventListener("change", updatePage);

    return () => {
      document
        .getElementById("pageNumber")
        ?.removeEventListener("change", updatePage);
    };
  }, [pageNum])

  // Keep optinModalOpenRef current so interval closures always see the live value
  useEffect(() => {
    optinModalOpenRef.current =
      getCookie("optin", {})[trakingData?.tlb?.token]?.show === "display";
  }, [trakingData]);

  // Listen for visibility changes to update page visibility state in real-time
  useEffect(() => {
    const checkVisibility = () => checkPageVisibility();

    const handleVisibilityChange = () => {
      isPageVisible.current = checkVisibility();
    };
    const handleFocus = () => { isPageVisible.current = checkVisibility(); };
    const handleBlur  = () => { isPageVisible.current = checkVisibility(); };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);
    window.addEventListener('blur', handleBlur);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('blur', handleBlur);
    };
  }, []);

  const actionCableSendData = (objData, strAction) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      const data = {
        command: "message",
        identifier: JSON.stringify({
          channel: "SessionMonitoringChannel",
          data: { session_id: window.objInit?.sess_id },
        }),
        ...(objData && {
          data: JSON.stringify({ message: objData, action: strAction }),
        }),
      };

      return wsRef.current.send(JSON.stringify(data), strAction);
    }
  };

  const initActionCableSessionData = (page) => {
    if (undefined === actionCableSessionData.page) {
      initSessionData(page);
    } else if (actionCableSessionData.page != page) {
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN && actionCableSessionDataRef.current?.id) {
        const actionSentPromise = actionCableSendData(
          actionCableSessionData,
          "save_info"
        );
        if (false !== actionSentPromise && actionSentPromise?.then) {
          actionSentPromise.then(function () {
            initSessionData(page);
          });
        } else {
          initSessionData(page);
        }
      }
    }
    else {
    initSessionData(page);
  }
  };

  const initSessionData = (page) => {
    const objInit = window?.objInit;
    lastSeenPageCookie[objInit.tlb?.token] = page;
    setCookie("lastSeenPage", lastSeenPageCookie);
    if (undefined !== objInit?.sess_id) {
      const existingId = actionCableSessionDataRef.current?.id;
      const existingPage = actionCableSessionDataRef.current?.page;

      const data = {
        session_id: objInit.sess_id,
        tracking_link_id: objInit.tlb.id,
        doc_id: Number(objInit.doc_id),
        docs_file_id:
          Number(objInit?.active_files && Object.keys(objInit?.active_files)[0]),
        page: page,
        seconds_on_page: 1,
      };
      // Read cookie fresh each call — the init-time snapshot misses cookies
      // set after opt-in, so post-optin pages would be tracked without receiver_id.
      const currentReceiverCookie = getCookie("receiver", {});
      if (currentReceiverCookie[objInit.tlb.token]?.id) {
        data.receiver_id = currentReceiverCookie[objInit.tlb.token].id;
      }
      
      if (existingId && existingPage === page) {
        data.id = existingId;
      }
      
      const doSend = () => actionCableSendData(data, "save_info");
      const pendingPage = pendingDebouncePageRef.current;

      if (pendingPage === page) {
        // Debounce queued for the same page — cancel it, send once here.
        clearTimeout(pageCreateDebounceRef.current);
        pageCreateDebounceRef.current = null;
        pendingDebouncePageRef.current = null;
        setActionCableSessionData({ ...data });
        actionCableSessionDataRef.current = { ...data };
        doSend();
      } else if (pendingPage !== null) {
        // Debounce queued for a DIFFERENT page (PDF fired change before WS opened).
        // Each debounce now carries its own data snapshot and fires independently,
        // so we just wait 60 ms (past their 50 ms window) before applying landing-page
        // data, keeping timestamps distinct and refs consistent.
        setTimeout(() => {
          setActionCableSessionData({ ...data });
          actionCableSessionDataRef.current = { ...data };
          doSend();
        }, 60);
      } else {
        // No pending debounce — send immediately.
        setActionCableSessionData({ ...data });
        actionCableSessionDataRef.current = { ...data };
        doSend();
      }
    }
  };

  const actionCableOptIn = async () => {
    let id = actionCableSessionDataRef.current?.id;
    if (!id) id = await waitForSessionPageId(50, 100);
    if (id) {
      actionCableSendData({ ...actionCableSessionDataRef.current, id }, "opt_in_info");
    }
  };

  const getPageVisibility = () => checkPageVisibility();

  isPageVisible.current = getPageVisibility();

  // Keep optinModalOpenRef in sync with cookie on every render
  optinModalOpenRef.current =
    optinCookie[trakingData.tlb?.token]?.show === "display";

  const actionCableWinPause = () => {
    const snapshot = { ...actionCableSessionDataRef.current };
    // Use ref for id — avoids reading stale state
    snapshot.session_page_id = actionCableSessionDataRef.current.id;
    if (snapshot.id) {
      actionCableSendData(snapshot, "win_pause_info");
    }
  };

  const actionCableSetIntervals = () => {
    clearInterval(updateIntervalRef.current);
    clearInterval(sendDataIntervalRef.current);
    if (
      undefined !== window?.objInit?.tlb &&
      wsRef.current &&
      wsRef.current.readyState === WebSocket.OPEN
    ) {
      // Increment seconds_on_page directly in the ref — no React state update,
      // no re-render, no GC allocation every second.
      updateIntervalRef.current = setInterval(() => {
        isPageVisible.current = getPageVisibility();
        if (isPageVisible.current && !optinModalOpenRef.current) {
          actionCableSessionDataRef.current = {
            ...actionCableSessionDataRef.current,
            seconds_on_page: (actionCableSessionDataRef.current.seconds_on_page || 0) + 1,
          };
        }
      }, 1000);

      // Send accumulated data every 2s (unchanged timing).
      // Reads directly from the ref: no setState, no React re-render.
      // Skips entirely during orientation changes to avoid CPU spike on top
      // of PDF page remounts.
      sendDataIntervalRef.current = setInterval(() => {
        if (window.__kdf_orientation_changing) return;

        isPageVisible.current = getPageVisibility();
        const snapshot = actionCableSessionDataRef.current;

        if (isPageVisible.current) {
          // Update-only: never create rows here. Row creation happens via the
          // pageNumber input "change" event → updatePage. This guard prevents
          // duplicate rows when the interval fires before the server broadcast
          // assigns an id.
          if (snapshot?.page && snapshot?.id) {
            actionCableSendData(snapshot, "save_info");
          }
        } else if (!optinModalOpenRef.current) {
          actionCableWinPause();
        }
      }, 2000);
    }
  };

  const actionCableVideoMontior = (data) => {
    var actioncableData = JSON.stringify(actionCableSessionDataRef.current);
    actioncableData = JSON.parse(actioncableData);
    actioncableData.session_page_id = actionCableSessionDataRef.current.id;
    actioncableData.docs_element_id = data.id;
    actioncableData.watchedPercent = data.watchedPercent;
    actioncableData.watchedSeconds = data.watchedSeconds;
    actioncableData.duration = data.duration;
    if (actionCableSessionDataRef.current?.id)
      actionCableSendData(actioncableData, "save_video_info");
  };

const waitForSessionPageId = async (tries = 50, delay = 100) => {
  while (tries-- > 0) {
    const spid = actionCableSessionDataRef.current?.id;
    if (spid) return spid;
    await new Promise(r => setTimeout(r, delay));
  }
  return null;
};

const popupTrack = async (lightboxId, triggerType = 'exit') => {
  const tlb = window?.objInit?.tlb;
  const sid = actionCableSessionDataRef.current?.session_id || actionCableSessionDataRef.current?.id;
  let spid = actionCableSessionDataRef.current?.id;
  if (!spid) spid = await waitForSessionPageId(50, 120);

  const input = document.getElementById("pageNumber");
  const currentPage = input && Number(input.value) ? Number(input.value) : actionCableSessionDataRef.current?.page;

  if (!sid || !spid || !lightboxId || !tlb) {
    return;
  }

  const payload = {
    session_id: sid,
    session_page_id: spid,
    docs_lightbox_id: Number(lightboxId),
    tracking_link_id: tlb.id,
    page: currentPage || 1,
    clicked_after: actionCableSessionDataRef.current?.seconds_on_page || 0,
    trigger_type: triggerType,
  };

  try {
    await networkService('/statistics/save/lb_trigger.json', 'POST', payload);
  } catch (err) {
    // Error handling
  }
  actionCableSendData(payload, 'lb_trigger');
  actionCableSendData(payload, 'save_lb_click');
};


  // Page-change notifier — triggers listener re-attachment only.
  // Row creation is handled by the pageNumber input "change" event → updatePage.
  const sendScrollInfo = (page) => {
    setPageNum(page);
  };

  const saveDesiredActionClicks = (objData, strAction) => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;

    const data = {
      command: "message",
      identifier: JSON.stringify({
        channel: "SessionMonitoringChannel",
        data: { session_id: window.objInit?.sess_id },
      }),
      ...(objData && {
        data: JSON.stringify({ message: objData, action: strAction }),
      }),
    };
    if (wsRef.current) {
      return wsRef.current.send(JSON.stringify(data), strAction);
    }
  };

  const applyDesiredAction = (element) => {
    const frequency = element?.dropdown_data
      ? element?.dropdown_data?.frequency
      : null;

    // Use ref instead of stale state — ref is always current
    const data = {
      ...actionCableSessionDataRef.current,
      frequency_type: frequency,
      docs_element_id: Number(element?.id)
    };
    setActionCableSessionData({ ...data });
    if (
      (element.element_type_name === "Image" ||
        element.element_type_name === "Hotspot" ||
        element.element_type_name === "HTML Text" ||
        element.element_type_name === "Video" ||
        element.element_type_name === "Web URL") &&
      element.dropdown_data
    ) {
      const currentElement = document.getElementById(`div${element.id}`);
      if (element?.dropdown_data?.desiredaction) {
        if (currentElement) {
          saveDesiredActionClicks(
            data,
            "save_desired_action_frequency"
          );
        }
      }
    }
  };

  //when element is clicked
  const elemClick = (elemId, evt, element) => {
    if (
      evt == undefined ||
      evt.delegateTarget?.children[0]?.attributes[0]?.value !== "accept"
    ) {
      if (undefined !== evt) {
        evt.stopPropagation();
      }
      if (wsRef.current) {
        // Always read from ref — avoids stale state values for id/page/seconds
        const sessionRef = actionCableSessionDataRef.current;
        let actionCablePageClick;
        if (elemId === "download" || elemId === "download-pdf") {
          actionCablePageClick = {
            session_page_id: sessionRef.id,
            is_download: true,
            is_lang_changed: false,
            changed_lang: "",
            page: sessionRef.page,
            clicked_after: sessionRef.seconds_on_page,
          };
        } else {
          actionCablePageClick = {
            session_page_id: sessionRef.id,
            docs_element_id: elemId,
            clicked_after: sessionRef.seconds_on_page,
            element_page: element?.page,
            session_id: sessionRef.session_id,
          };
        }
        
        if (!sessionRef.id) {
          // ID not yet assigned by server — poll the ref until it arrives
          const sendClickInterval = setInterval(function () {
            const id = actionCableSessionDataRef.current?.id;
            if (id) {
              actionCablePageClick.session_page_id = id;
              clearInterval(sendClickInterval);
              actionCableSendData(actionCablePageClick, "save_click");
            }
          }, 100);
        } else {
          actionCableSendData(actionCablePageClick, "save_click");
        }
        applyDesiredAction(element);
      }
    }
  };

  return {
    sendScrollInfo,
    trackPageChange,
    elemClick,
    actionCableOptIn,
    setVideoData,
    actionCableSetIntervals,
    actionCableVideoMontior,
    saveDesiredActionClicks,
    applyDesiredAction,
    popupTrack,
    pageNum 
  };
};

export default useStatHook;
